import { when } from 'mobx'

import config from '@src/config'

import { getRootLogger } from '@odyssey/logging/logger'

export type VoiceService = {
  readonly hasActiveCalls: boolean
}

export type InitPreloadErrorListenerOptions = {
  voiceService: VoiceService
  reload?: () => void
  appVersion?: string
}

const RELOAD_DELAY_MS = 1000

// `{ once: true }` only dedupes within one page load, so a chunk failure that
// survives a reload (proxy/AV/service worker, truncated download) would loop
// forever. Cap auto-reloads per tab session, tracked in sessionStorage.
const MAX_RELOADS = 10

// Key the count by build version so a tab open across many deploys gets a
// fresh budget on each new build, instead of one bad deploy permanently
// disabling recovery for every later, unrelated deploy.
const RELOAD_COUNT_KEY_PREFIX = 'vitePreloadError.reloadCount.'

function getReloadCountKey(appVersion: string): string {
  return `${RELOAD_COUNT_KEY_PREFIX}${appVersion}`
}

type PreloadErrorEvent = Event & { payload?: unknown }

function describePreloadError(event: Event): {
  errorName: string
  errorMessage: string
} {
  const { payload } = event as PreloadErrorEvent
  // For a network failure the Error message embeds the failing module URL; for
  // a parse failure it's the SyntaxError text.
  if (payload instanceof Error) {
    return { errorName: payload.name, errorMessage: payload.message }
  }
  // Non-Error payloads are unexpected; avoid a useless "[object Object]".
  if (typeof payload === 'string') {
    return { errorName: 'UnknownError', errorMessage: payload }
  }
  return {
    errorName: 'UnknownError',
    errorMessage: `Non-Error payload (${typeof payload})`,
  }
}

function getReloadCount(appVersion: string): number {
  try {
    const raw = window.sessionStorage.getItem(getReloadCountKey(appVersion))
    const parsed = raw === null ? 0 : Number.parseInt(raw, 10)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
  } catch {
    // Storage can be unavailable (private mode, disabled, hostile middlebox).
    // Degrade to "no count": we lose loop protection but never break reloads.
    return 0
  }
}

function setReloadCount(count: number, appVersion: string): void {
  try {
    const currentKey = getReloadCountKey(appVersion)
    window.sessionStorage.setItem(currentKey, String(count))

    // Drop counts from older builds now that this tab is running a newer one.
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const key = window.sessionStorage.key(i)
      if (key && key !== currentKey && key.startsWith(RELOAD_COUNT_KEY_PREFIX)) {
        window.sessionStorage.removeItem(key)
      }
    }
  } catch {
    // best-effort; see getReloadCount
  }
}

/**
 * Reload the page when a Vite chunk preload fails (typically after a deploy
 * invalidates the current session's chunk URLs), deferring while the user is
 * on a call. Sibling to AppUpdateController, which gates the "new version
 * available" reload path on the same call state.
 */
export function initPreloadErrorListener({
  voiceService,
  reload = () => window.location.reload(),
  appVersion = config.FULL_VERSION,
}: InitPreloadErrorListenerOptions): () => void {
  let disposeWhen: (() => void) | null = null
  let reloadTimer: ReturnType<typeof setTimeout> | null = null

  const handler = (event: Event): void => {
    const log = getRootLogger().getChild('vite:preloadError')
    const { errorName, errorMessage } = describePreloadError(event)

    // A SyntaxError means the browser couldn't parse the chunk: either the bytes
    // are corrupted (truncated/substituted in transit) or they're valid JS using
    // syntax this (older) browser doesn't support. Either way a reload re-runs
    // the same bytes in the same browser and won't help, so skip it and log
    // distinctly. Fetch failures (TypeError) fall through to the reload path.
    if (errorName === 'SyntaxError') {
      log.warn('chunk parse error; not reloading', {
        errorName,
        errorMessage,
        wasOnCall: voiceService.hasActiveCalls,
      })
      return
    }

    const reloadCount = getReloadCount(appVersion)

    // Stop once we've hit the cap; log so the bail-out is visible in Datadog.
    if (reloadCount >= MAX_RELOADS) {
      log.warn('reload cap reached; not reloading', {
        errorName,
        errorMessage,
        reloadCount,
        wasOnCall: voiceService.hasActiveCalls,
      })
      return
    }

    log.warn('scheduling reload', {
      errorName,
      errorMessage,
      reloadCount,
      wasOnCall: voiceService.hasActiveCalls,
    })

    // Schedule the reload as soon as no call is active. `when` resolves
    // synchronously if the predicate is already true, so for users not on a
    // call the 1s reload timer starts right away.
    disposeWhen = when(
      () => !voiceService.hasActiveCalls,
      () => {
        // Brief delay so the call-ended dialog can finish dismissing before
        // we tear the page down.
        reloadTimer = setTimeout(() => {
          // Persist before reloading so the next page load sees the bump.
          setReloadCount(reloadCount + 1, appVersion)
          log.warn('reloading', { reloadCount: reloadCount + 1 })
          reload()
        }, RELOAD_DELAY_MS)
      },
    )
  }

  // `{ once: true }` is the dedupe: a burst of preload errors only invokes
  // the handler for the first event; the rest are ignored at the listener
  // layer.
  window.addEventListener('vite:preloadError', handler, { once: true })

  return () => {
    window.removeEventListener('vite:preloadError', handler)
    disposeWhen?.()
    if (reloadTimer !== null) {
      clearTimeout(reloadTimer)
    }
  }
}
